import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import { DownloadOptions } from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import ExportDataDropDown from '@/components/molecules/ExportDataDropDown/ExportDataDropDown';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { useGetRespiration } from '@/domain/behavior';
import { isValidCageId } from '@/domain/cages';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';

import BehaviorRespirationView from './BehaviorRespirationView';
import { prepareRespirationExportData } from './helpers';
import { useRespirationChartData } from './useRespirationChartData';

export default function BehaviorRespirationContainer() {
    const { t } = useTranslation('behaviorModule');
    const { cageId } = useCage();
    const cageName = useCageName();
    const { selectedFishGroupId } = useCageFilter();

    const { dateFrom, dateTo, selectedPresetId, onDateChange } = useDateRangePicker({
        key: 'respiration',
        defaultPresetId: 'last7days',
    });

    const respirationParams = useMemo(
        () => ({
            from: dateFrom,
            to: dateTo,
            showHourly: selectedPresetId === '24h',
            fishGroupId: selectedFishGroupId,
        }),
        [dateFrom, dateTo, selectedPresetId, selectedFishGroupId]
    );

    const query = useGetRespiration(cageId, respirationParams, {
        enabled: isValidCageId(cageId),
    });
    const { data, isLoading } = query;

    const isFetchingData = !isValidCageId(cageId) || isLoading;

    const respirationDataPoints = data?.data ?? [];
    const lastMeasurement = data?.lastMeasurement ?? '';
    const chart = useRespirationChartData({ respirationDataPoints });

    const { columns: exportColumns, data: exportData } = useMemo(
        () => prepareRespirationExportData(respirationDataPoints),
        [respirationDataPoints]
    );

    const exportActions = (
        <ExportDataDropDown disabled={isFetchingData}>
            <DownloadOptions
                data={exportData}
                columns={exportColumns}
                nameToInclude="Respiration"
            />
        </ExportDataDropDown>
    );

    return (
        <PageLayout title={cageName} subtitle={t('respirationView.title')} actions={exportActions}>
            <BehaviorRespirationView
                chart={chart}
                isLoading={isFetchingData}
                error={query.error}
                retry={retryFailedQueries(query)}
                dateFrom={dateFrom}
                dateTo={dateTo}
                lastMeasurement={lastMeasurement}
                onDateChange={onDateChange}
            />
        </PageLayout>
    );
}
